import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Bell,
  BookOpen,
  Briefcase,
  Building2,
  ChevronDown,
  CreditCard,
  FileText,
  FolderOpen,
  Home,
  Landmark,
  ListTodo,
  LogOut,
  Menu,
  MessageSquare,
  PiggyBank,
  Receipt,
  TrendingUp,
  Settings,
  User,
  Users,
  X,
  ChevronsLeft,
  ChevronsRight,
  ExternalLink,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { PageTitleOverrideProvider } from "../contexts/PageTitleOverrideProvider";
import { usePageTitleOverride } from "../contexts/pageTitleOverride";
import ErrorBoundary from "../components/ErrorBoundary";
import { SidebarNavLink } from "../components/nav/SidebarNavLink";
import { useNavPendingModules } from "../hooks/useNavPendingModules";
import { usePermissions } from "../hooks/usePermissions";
import { getRouteGateConfig } from "../lib/permissions";
import { usePendingAccessRequestCount } from "../hooks/useAccessRequests";
import { apiHelpers } from "../lib/api";
import { setForbiddenToastRoute } from "../lib/forbiddenToast";
import { resolvePageTitle } from "../lib/pageTitle";
import { cn } from "../lib/utils";
import { useAuthStore } from "../stores/authStore";

type NotificationItem = {
  id: number;
  title: string;
  message: string;
  notification_type: string;
  link: string;
  is_read: boolean;
  created_at: string;
};

function normalizeNotifications(payload: unknown): NotificationItem[] {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray((payload as { results?: unknown }).results)) {
    return (payload as { results: NotificationItem[] }).results;
  }

  return [];
}

export default function MainLayout() {
  return (
    <PageTitleOverrideProvider>
      <MainLayoutContent />
    </PageTitleOverrideProvider>
  );
}

function MainLayoutContent() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem("sidebar-collapsed") === "true",
  );
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const mobileMenuButtonRef = useRef<HTMLButtonElement>(null);
  const userMenuButtonRef = useRef<HTMLButtonElement>(null);
  const notificationsButtonRef = useRef<HTMLButtonElement>(null);
  const { user, logout, isLoading, isAuthenticated } = useAuthStore();
  const { isNavItemVisible, canAccessPath } = usePermissions();
  const { pendingByModule } = useNavPendingModules();
  const profilePending = isLoading || (isAuthenticated && !user);
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { data: pendingAccessCount = 0 } = usePendingAccessRequestCount();
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    setForbiddenToastRoute(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    if (!sidebarOpen && !userMenuOpen && !notificationsOpen) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") {
        return;
      }
      if (userMenuOpen) {
        event.preventDefault();
        setUserMenuOpen(false);
        userMenuButtonRef.current?.focus();
        return;
      }
      if (notificationsOpen) {
        event.preventDefault();
        setNotificationsOpen(false);
        notificationsButtonRef.current?.focus();
        return;
      }
      if (sidebarOpen) {
        event.preventDefault();
        setSidebarOpen(false);
        mobileMenuButtonRef.current?.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [sidebarOpen, userMenuOpen, notificationsOpen]);

  const { data: unreadCount = 0 } = useQuery({
    queryKey: ["notifications", "unread-count"],
    queryFn: async () => {
      const response = await apiHelpers.notifications.unreadCount();
      return response.data?.unread_count ?? 0;
    },
  });

  const { data: notifications = [], isLoading: notificationsLoading } =
    useQuery({
      queryKey: ["notifications", "list"],
      queryFn: async () => {
        const response = await apiHelpers.notifications.list();
        return normalizeNotifications(response.data);
      },
      enabled: notificationsOpen,
    });

  const markReadMutation = useMutation({
    mutationFn: (id: number) => apiHelpers.notifications.markRead(id),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["notifications", "list"] }),
        queryClient.invalidateQueries({
          queryKey: ["notifications", "unread-count"],
        }),
      ]);
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: () => apiHelpers.notifications.markAllRead(),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["notifications", "list"] }),
        queryClient.invalidateQueries({
          queryKey: ["notifications", "unread-count"],
        }),
      ]);
    },
  });

  const visibleNotifications = useMemo(
    () => notifications.slice(0, 8),
    [notifications],
  );

  const ghostfolioUrl = `${window.location.protocol}//${window.location.hostname}:3333`;
  const navigation = useMemo(
    () =>
      [
        { name: t("nav.home"), href: "/", icon: Home },
        {
          name: t("nav.documents"),
          href: "/documents",
          icon: FileText,
          children: [
            {
              name: t("nav.documentQueue"),
              href: "/documents/queue",
              adminOnly: true,
            },
          ],
        },
        { name: t("nav.properties"), href: "/properties", icon: Building2 },
        {
          name: t("nav.utilityMigrations"),
          href: "/utility-migrations",
          icon: Receipt,
        },
        { name: t("nav.bundles"), href: "/bundles", icon: FolderOpen },
        { name: t("nav.family"), href: "/family", icon: Users },
        {
          name: t("nav.payments"),
          href: "/payments",
          icon: CreditCard,
          children: [
            {
              name: t("nav.hoaCharges"),
              href: "/payments/hoa",
              icon: Receipt,
            },
          ],
        },
        {
          name: t("nav.budget"),
          href: "/budget",
          icon: PiggyBank,
          children: [
            {
              name: t("nav.estatePlanner"),
              href: "/budget/planner",
              icon: Landmark,
            },
          ],
        },
        { name: t("nav.tasks"), href: "/tasks", icon: ListTodo },
        { name: t("nav.finances"), href: "/finances", icon: TrendingUp },
        { name: t("nav.career"), href: "/career", icon: Briefcase },
        { name: t("nav.knowledge"), href: "/knowledge", icon: BookOpen },
      ],
    [t],
  );

  const financesGranted = canAccessPath("/finances");
  const aiChatGate = getRouteGateConfig("/ai-chat");
  const aiChatGranted = canAccessPath("/ai-chat");

  const settingsNavigation = [
    { name: t("nav.settings"), href: "/settings", icon: Settings },
  ];

  const { override: pageTitleOverride } = usePageTitleOverride();

  const pageTitle = useMemo(
    () =>
      pageTitleOverride ??
      resolvePageTitle(
        location.pathname,
        navigation,
        [{ name: t("nav.settings"), href: "/settings" }],
        [{ pathname: "/ai-chat", name: t("nav.aiChat") }],
        t("nav.appName"),
      ),
    [location.pathname, navigation, pageTitleOverride, t],
  );

  const handleLogout = () => {
    logout();
  };

  const toggleSidebarCollapsed = () => {
    setSidebarCollapsed((prev) => {
      localStorage.setItem("sidebar-collapsed", String(!prev));
      return !prev;
    });
  };

  const handleNotificationClick = async (notification: NotificationItem) => {
    if (!notification.is_read) {
      await markReadMutation.mutateAsync(notification.id);
    }

    if (notification.link) {
      navigate(notification.link);
    }

    setNotificationsOpen(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-900/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        id="main-sidebar"
        className={cn(
          "fixed inset-y-0 left-0 z-50 bg-white dark:bg-gray-800 shadow-lg transform transition-all duration-300 lg:translate-x-0",
          sidebarCollapsed ? "lg:w-16 w-64" : "w-64",
          sidebarOpen ? "translate-x-0" : "-translate-x-full",
        )}
        {...(sidebarOpen
          ? { role: "dialog", "aria-modal": true, "aria-label": t("common.openMenu") }
          : {})}
      >
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3 min-w-0">
            <span className="text-2xl shrink-0">🏠</span>
            <span
              className={cn(
                "text-xl font-bold text-gray-900 dark:text-white whitespace-nowrap transition-all duration-300",
                sidebarCollapsed && "lg:hidden",
              )}
            >
              FamilyHub
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button
              className="hidden lg:flex p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
              onClick={toggleSidebarCollapsed}
              title={sidebarCollapsed ? t("nav.expandSidebar") : t("nav.collapseSidebar")}
            >
              {sidebarCollapsed ? (
                <ChevronsRight className="w-4 h-4" />
              ) : (
                <ChevronsLeft className="w-4 h-4" />
              )}
            </button>
            <button
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4">
          <div className="px-3 space-y-1">
            {profilePending
              ? [...Array(8)].map((_, i) => (
                  <div
                    key={i}
                    className="h-10 rounded-lg skeleton mx-0"
                    aria-hidden
                  />
                ))
              : navigation.map((item) => {
              const isActive =
                item.href === "/"
                  ? location.pathname === "/"
                  : location.pathname.startsWith(item.href);
              const gate = getRouteGateConfig(item.href);
              const granted = item.href === "/" ? true : canAccessPath(item.href);
              const pending =
                gate?.accessRequestModule != null
                  ? (pendingByModule.get(gate.accessRequestModule) ?? null)
                  : null;
              const visibleChildren = (item.children ?? []).filter((child) =>
                isNavItemVisible(child.href, {
                  adminOnly: (child as { adminOnly?: boolean }).adminOnly,
                }),
              );
              return (
                <div key={item.name}>
                  <SidebarNavLink
                    href={item.href}
                    name={item.name}
                    icon={item.icon}
                    granted={granted}
                    requestable={gate?.requestable ?? false}
                    moduleKey={gate?.accessRequestModule ?? null}
                    pending={pending}
                    isActive={isActive}
                    collapsed={sidebarCollapsed}
                    end={item.href === "/"}
                    onNavigate={() => setSidebarOpen(false)}
                  />

                  {visibleChildren.length > 0 && !sidebarCollapsed && (
                    <div className="ml-4 mt-0.5 space-y-0.5 border-l border-gray-200 dark:border-gray-700 pl-3">
                      {visibleChildren.map((child) => {
                        const childGate = getRouteGateConfig(child.href);
                        const childGranted = canAccessPath(child.href);
                        const childPending =
                          childGate?.accessRequestModule != null
                            ? (pendingByModule.get(childGate.accessRequestModule) ?? null)
                            : null;
                        const childActive = location.pathname === child.href;
                        return (
                          <SidebarNavLink
                            key={child.href}
                            href={child.href}
                            name={child.name}
                            icon={
                              (child as { icon?: typeof Receipt }).icon ?? Receipt
                            }
                            granted={childGranted}
                            requestable={childGate?.requestable ?? false}
                            moduleKey={childGate?.accessRequestModule ?? null}
                            pending={childPending}
                            isActive={childActive}
                            collapsed={false}
                            onNavigate={() => setSidebarOpen(false)}
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-8 px-3">
            <div
              className={cn(
                "text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 px-3",
                sidebarCollapsed && "lg:hidden",
              )}
            >
              {t("nav.configure")}
            </div>
            <div className="space-y-1">
              {profilePending
                ? [...Array(2)].map((_, i) => (
                    <div
                      key={i}
                      className="h-10 rounded-lg skeleton"
                      aria-hidden
                    />
                  ))
                : settingsNavigation.map((item) => {
                const isActive = location.pathname.startsWith(item.href);
                return (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    onClick={() => setSidebarOpen(false)}
                    title={sidebarCollapsed ? item.name : undefined}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                      sidebarCollapsed && "lg:justify-center lg:px-2",
                      isActive
                        ? "bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400"
                        : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700",
                    )}
                  >
                    <item.icon className="w-5 h-5 shrink-0" />
                    <span
                      className={cn(
                        "flex items-center gap-2 min-w-0",
                        sidebarCollapsed && "lg:hidden",
                      )}
                    >
                      {item.name}
                      {isAdmin && pendingAccessCount > 0 ? (
                        <span
                          className="inline-flex min-w-[1.25rem] justify-center rounded-full bg-amber-500 px-1.5 py-0.5 text-xs font-semibold text-white"
                          aria-label={t("accessRequests.settings.pendingBadge", {
                            count: pendingAccessCount,
                          })}
                        >
                          {pendingAccessCount}
                        </span>
                      ) : null}
                    </span>
                  </NavLink>
                );
              })}
            </div>
          </div>

          {/* External tools */}
          {!profilePending && (
          <div className="mt-4 px-3">
            <div
              className={cn(
                "text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 px-3",
                sidebarCollapsed && "lg:hidden",
              )}
            >
              {t("nav.tools")}
            </div>
            {financesGranted ? (
              <a
                href={ghostfolioUrl}
                target="_blank"
                rel="noopener noreferrer"
                title={sidebarCollapsed ? t("nav.ghostfolio") : undefined}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700",
                  sidebarCollapsed && "lg:justify-center lg:px-2",
                )}
              >
                <TrendingUp className="w-5 h-5 shrink-0" />
                <span className={cn("flex-1", sidebarCollapsed && "lg:hidden")}>
                  {t("nav.ghostfolio")}
                </span>
                <ExternalLink className={cn("w-3 h-3 opacity-50", sidebarCollapsed && "lg:hidden")} />
              </a>
            ) : (
              <span
                role="link"
                aria-disabled="true"
                aria-label={`${t("nav.ghostfolio")}, ${t("nav.restrictedAccess", { defaultValue: "acces restricționat" })}`}
                title={sidebarCollapsed ? t("nav.ghostfolio") : undefined}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium opacity-55 saturate-50 blur-[0.3px] cursor-not-allowed text-gray-500 dark:text-gray-400",
                  sidebarCollapsed && "lg:justify-center lg:px-2",
                )}
                data-testid="nav-restricted-ghostfolio"
              >
                <TrendingUp className="w-5 h-5 shrink-0" aria-hidden />
                <span className={cn("flex-1", sidebarCollapsed && "lg:hidden")}>
                  {t("nav.ghostfolio")}
                </span>
              </span>
            )}
            <SidebarNavLink
              href="/ai-chat"
              name={t("nav.aiChat")}
              icon={MessageSquare}
              granted={aiChatGranted}
              requestable={aiChatGate?.requestable ?? true}
              moduleKey={aiChatGate?.accessRequestModule ?? null}
              pending={
                aiChatGate?.accessRequestModule
                  ? (pendingByModule.get(aiChatGate.accessRequestModule) ?? null)
                  : null
              }
              isActive={location.pathname === "/ai-chat"}
              collapsed={sidebarCollapsed}
              onNavigate={() => setSidebarOpen(false)}
            />
          </div>
          )}
        </nav>

        {/* User section */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-4">
          <div
            className={cn(
              "flex items-center gap-3",
              sidebarCollapsed && "lg:justify-center",
            )}
          >
            <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center shrink-0">
              <User className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div
              className={cn(
                "flex-1 min-w-0",
                sidebarCollapsed && "lg:hidden",
              )}
            >
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                {user?.first_name} {user?.last_name}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {user?.email}
              </p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div
        className={cn(
          "transition-all duration-300",
          sidebarCollapsed ? "lg:pl-16" : "lg:pl-64",
        )}
      >
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between h-16 px-4">
            {/* Mobile menu button */}
            <button
              ref={mobileMenuButtonRef}
              type="button"
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => setSidebarOpen(true)}
              aria-expanded={sidebarOpen}
              aria-controls="main-sidebar"
              aria-label={t("common.openMenu")}
            >
              <Menu className="w-6 h-6" />
            </button>

            <div className="min-w-0 flex-1 px-2 lg:flex-none lg:px-0">
              <h1 className="truncate text-lg font-semibold text-gray-900 dark:text-white">
                {pageTitle}
              </h1>
            </div>

            {/* Right side */}
            <div className="flex items-center gap-2">
              {/* Notifications */}
              <div className="relative">
                <button
                  ref={notificationsButtonRef}
                  type="button"
                  onClick={() => {
                    setNotificationsOpen((current) => !current);
                    setUserMenuOpen(false);
                  }}
                  className="relative p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                  aria-expanded={notificationsOpen}
                  aria-haspopup="dialog"
                  aria-label={t("common.notifications")}
                >
                  <Bell className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  {unreadCount > 0 && (
                    <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full" />
                  )}
                </button>

                {notificationsOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setNotificationsOpen(false)}
                    />
                    <div
                      role="dialog"
                      aria-label={t("common.notifications")}
                      className="absolute right-0 z-20 w-96 mt-2 overflow-hidden bg-white border border-gray-200 shadow-lg dark:bg-gray-800 rounded-xl dark:border-gray-700"
                    >
                      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {t("common.notifications")}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {t("notifications.unreadCount", {
                              count: unreadCount,
                            })}
                          </p>
                        </div>
                        <button
                          onClick={() => markAllReadMutation.mutate()}
                          disabled={
                            unreadCount === 0 || markAllReadMutation.isPending
                          }
                          className="text-sm text-primary-600 disabled:opacity-50"
                        >
                          {t("notifications.markAllRead")}
                        </button>
                      </div>

                      <div className="max-h-96 overflow-auto">
                        {notificationsLoading ? (
                          <div className="flex items-center justify-center p-6">
                            <div className="w-6 h-6 border-2 border-primary-500 rounded-full border-t-transparent animate-spin" />
                          </div>
                        ) : visibleNotifications.length === 0 ? (
                          <div className="p-6 text-sm text-center text-gray-500 dark:text-gray-400">
                            {t("notifications.empty")}
                          </div>
                        ) : (
                          visibleNotifications.map((notification) => (
                            <button
                              key={notification.id}
                              onClick={() =>
                                handleNotificationClick(notification)
                              }
                              className="w-full px-4 py-3 text-left border-b border-gray-100 dark:border-gray-700 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-700/40"
                            >
                              <div className="flex items-start gap-3">
                                <div
                                  className={`mt-1 h-2.5 w-2.5 rounded-full ${notification.is_read ? "bg-gray-300 dark:bg-gray-600" : "bg-red-500"}`}
                                />
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center justify-between gap-3">
                                    <p className="font-medium text-gray-900 dark:text-white truncate">
                                      {notification.title}
                                    </p>
                                    <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0">
                                      {new Date(
                                        notification.created_at,
                                      ).toLocaleString()}
                                    </span>
                                  </div>
                                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                                    {notification.message}
                                  </p>
                                </div>
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* User menu */}
              <div className="relative">
                <button
                  ref={userMenuButtonRef}
                  type="button"
                  onClick={() => {
                    setUserMenuOpen(!userMenuOpen);
                    setNotificationsOpen(false);
                  }}
                  className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                  aria-expanded={userMenuOpen}
                  aria-haspopup="menu"
                  aria-label={t("common.userMenu")}
                >
                  <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                    <User className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                  </div>
                  <ChevronDown className="w-4 h-4 text-gray-500" />
                </button>

                {/* Dropdown */}
                {userMenuOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setUserMenuOpen(false)}
                    />
                    <div
                      role="menu"
                      className="absolute right-0 mt-2 z-20 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1"
                    >
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <LogOut className="w-4 h-4" />
                        {t("nav.logout")}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">
          <ErrorBoundary resetKey={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}
